/**
 * The parts under a scene object as they stand now, in another object's frame.
 * The one place the fit geometry reads Babylon.
 */

import { Matrix } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { SceneObject } from '../../core/SceneObject';
import { Text } from '../../exports/behaviors';
import { getPrimitiveType, getSavedPrimitiveType } from '../../export/BuiltInPrimitives';
import { isDrawn } from '../quality/measure';
import { fitPart, type FitPart } from './parts';
import { fitShapes, placeShape, type FitShape } from './solids';

/** A matrix entry in a pose key. A hair below zero (what inverting a moved frame leaves) is zero, not "-0.000000". */
const fixed = (v: number): string => (Math.abs(v) < 5e-7 ? '0.000000' : v.toFixed(6));

/** A part as it stands: its geometry in the frame, and a key that is equal exactly when its pose in the frame is. */
export interface FitPose {
    readonly part: FitPart;
    readonly key: string;
}

// Keyed by the position vertex buffer: a mesh rebuilt or given new vertices gets a new buffer and is split
// again. Vertices rewritten in place in the same buffer keep the first split.
const shapesByBuffer = new WeakMap<object, FitShape[]>();
const partsByObject = new WeakMap<SceneObject, Map<string, FitPart>>();
const ids = new WeakMap<object, number>();
let nextId = 0;

function bufferOf(mesh: AbstractMesh): object {
    const source = (mesh as InstancedMesh).sourceMesh ?? (mesh as Mesh);
    return source.getVertexBuffer?.('position') ?? source;
}

function shapesOf(mesh: AbstractMesh): FitShape[] {
    const buffer = bufferOf(mesh);
    let shapes = shapesByBuffer.get(buffer);
    if (!shapes) {
        const positions = mesh.getVerticesData('position'), indices = mesh.getIndices();
        shapes = positions && indices ? fitShapes(positions, indices) : [];
        shapesByBuffer.set(buffer, shapes);
    }
    return shapes;
}

function geometryId(mesh: AbstractMesh): number {
    const buffer = bufferOf(mesh);
    let id = ids.get(buffer);
    if (id === undefined) ids.set(buffer, id = nextId++);
    return id;
}

/** Drawn, with geometry, not squashed to a point (a step scaled to 0 until its page), and not skinned, morphed or thin-instanced. */
function shows(mesh: AbstractMesh): boolean {
    if (!isDrawn(mesh) || mesh.skeleton || mesh.morphTargetManager || ((mesh as Mesh).thinInstanceCount ?? 0) > 0) return false;
    const box = mesh.getBoundingInfo().boundingBox;
    return box.minimumWorld.subtract(box.maximumWorld).length() > 0;
}

/** A built-in shape: its src names one (live or as saved), or its meshes are the built-in ones. */
function isPrimitive(object: SceneObject): boolean {
    const src = object.src;
    if (src && (getPrimitiveType(src) ?? getSavedPrimitiveType(src))) return true;
    const meshes = object.getOwnedMeshes();
    return meshes.length > 0 && meshes.every(m => m.name.startsWith('__builtin_'));
}

/**
 * A group of bare primitives (no behaviors, no meshes of its own, only childless built-in shapes) is one
 * part made of pieces, as a baked prototype is one part made of solids.
 */
function isPrimitiveGroup(group: SceneObject): boolean {
    const children = group.getChildren();
    return children.length > 0 && group.getBehaviors().length === 0 && group.getOwnedMeshes().length === 0
        && children.every(c => c.getChildren().length === 0 && isPrimitive(c));
}

/** The meshes that show now under `roots`, by the part that owns them (see `partsIn`). */
function shownMeshes(roots: readonly SceneObject[]): Map<SceneObject, AbstractMesh[]> {
    const meshesOf = new Map<SceneObject, AbstractMesh[]>();
    for (const object of new Set(roots.flatMap(root => [root, ...root.getDescendants()]))) {
        if (object.getBehavior(Text)) continue;
        const meshes = object.getOwnedMeshes().filter(m => { m.computeWorldMatrix(true); return shows(m); });
        if (!meshes.length) continue;
        const parent = object.getParent();
        const owner = parent && !roots.includes(object) && isPrimitiveGroup(parent) ? parent : object;
        const list = meshesOf.get(owner);
        if (list) list.push(...meshes); else meshesOf.set(owner, meshes);
    }
    return meshesOf;
}

/** The parts under `roots` that show now, as `partsIn` keys them, without reading their geometry. */
export function partsShown(roots: readonly SceneObject[]): Set<SceneObject> {
    return new Set(shownMeshes(roots).keys());
}

/**
 * Every part under `roots` that shows now, posed in `frame`'s space and keyed by the object that owns it:
 * each object that owns drawn meshes, with a group of bare primitives counted as one part. Text is not a
 * part, and skinned, morphed or thin-instanced meshes are left out. A part's up is its owner's +Y. The caller
 * picks what shows (a page, Base) before calling.
 */
export function partsIn(frame: SceneObject, roots: readonly SceneObject[]): Map<SceneObject, FitPose> {
    frame.transform.computeWorldMatrix(true);
    const toFrame = Matrix.Invert(frame.transform.getWorldMatrix());
    const out = new Map<SceneObject, FitPose>();
    for (const [object, meshes] of shownMeshes(roots)) {
        const matrices = meshes.map(m => m.getWorldMatrix().multiply(toFrame));
        // The pose, and which geometry: new vertices under an unchanged pose are a new part.
        const key = matrices.map((m, i) => `${geometryId(meshes[i])}:${Array.from(m.m, fixed).join(',')}`).join(';');
        let cache = partsByObject.get(object);
        if (!cache) partsByObject.set(object, cache = new Map<string, FitPart>());
        let part = cache.get(key);
        if (!part) {
            const solids = meshes.flatMap((mesh, i) => shapesOf(mesh).map(shape => placeShape(shape, matrices[i].m)));
            object.transform.computeWorldMatrix(true);
            const own = object.transform.getWorldMatrix().multiply(toFrame).m;
            part = fitPart(solids, [own[4], own[5], own[6]]);
            if (cache.size > 64) cache.clear();
            cache.set(key, part);
        }
        out.set(object, { part, key });
    }
    return out;
}
